import React, {ChangeEvent, useRef, useState} from "react";
import {CallbackErrorModalFL, CallbackFinishedModalFL} from "../../Dashboard/Form/ModalForm";
import {UploadBusiness} from "../../../service/Business/BusinessService";
import {getIdWithToken} from "../../../utils/storage/Token";
import {EnumTypeUpload} from "../../../types/Business/EnumTypeUpload";
import {UploadBilling, UploadBL} from "../../../service/Business/BillingService";
import {UploadExpense} from "../../../service/Business/ExpenseService";
import {ArchiveType, CheckArchiveExists} from "../../../service/Business/ArchiveService";
import FCAlertMsg, {TypeAlertMsg} from "../../FCObjets/FCAlertMsg/FCAlertMsg";
import "./FormAtelier.scss";

type FormUploadFileProps = {
    onFinished: CallbackFinishedModalFL;
    onError: CallbackErrorModalFL;
    type: EnumTypeUpload;
    idBusiness?: number;
    idBilling?: number;
    idExpense?: number;
};

const FormUploadFile: React.FunctionComponent<FormUploadFileProps> = (props) => {
    const [file, setFile] = useState<File | undefined>();
    const [confirmOverwrite, setConfirmOverwrite] = useState<boolean>(false);
    const [checking, setChecking] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    const accept = props.type === EnumTypeUpload.ARCHIVE_EXPENSE
        ? "image/png, image/jpeg, .pdf"
        : ".pdf";

    const hint = props.type === EnumTypeUpload.ARCHIVE_EXPENSE
        ? "Formats acceptés : PDF, JPG, PNG (max 10 Mo)"
        : "Format accepté : PDF (max 10 Mo)";

    const targetLabel =
        props.type === EnumTypeUpload.ARCHIVE_BUSINESS ? "un contrat" :
            props.type === EnumTypeUpload.ARCHIVE_BL ? "un bon de livraison" :
                props.type === EnumTypeUpload.ARCHIVE_BILLING ? "une facture" :
                    props.type === EnumTypeUpload.ARCHIVE_EXPENSE ? "un justificatif" : "un fichier";

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selectedFile = e.target.files[0];
            if (selectedFile && selectedFile.size > MAX_FILE_SIZE) {
                props.onError({message: "Le fichier dépasse la taille maximale autorisée (10 Mo)."});
                e.target.value = "";
                return;
            }
            setFile(selectedFile);
            setConfirmOverwrite(false);
        }
    };

    const performUpload = () => {
        if (!file) return;

        if (props.type === EnumTypeUpload.ARCHIVE_BUSINESS && props.idBusiness) {
            UploadBusiness(getIdWithToken(), props.idBusiness, file)
                .then(() => props.onFinished("Contrat archivé !"))
                .catch((err) => props.onError(err));
        } else if (props.type === EnumTypeUpload.ARCHIVE_BILLING && props.idBilling) {
            UploadBilling(getIdWithToken(), props.idBilling, file)
                .then(() => props.onFinished("Facture archivée !"))
                .catch((err) => props.onError(err));
        } else if (props.type === EnumTypeUpload.ARCHIVE_BL && props.idBilling) {
            UploadBL(getIdWithToken(), props.idBilling, file)
                .then(() => props.onFinished("Bon de livraison archivé !"))
                .catch((err) => props.onError(err));
        } else if (props.type === EnumTypeUpload.ARCHIVE_EXPENSE && props.idExpense) {
            UploadExpense(getIdWithToken(), props.idExpense, file)
                .then(() => props.onFinished("Dépense archivée !"))
                .catch((err) => props.onError(err));
        }
    };

    const getArchiveType = (): { type: ArchiveType; id: number } | null => {
        if (props.type === EnumTypeUpload.ARCHIVE_BUSINESS && props.idBusiness)
            return {type: "BUSINESS", id: props.idBusiness};
        if (props.type === EnumTypeUpload.ARCHIVE_BL && props.idBilling)
            return {type: "BL", id: props.idBilling};
        if (props.type === EnumTypeUpload.ARCHIVE_BILLING && props.idBilling)
            return {type: "BILLING", id: props.idBilling};
        if (props.type === EnumTypeUpload.ARCHIVE_EXPENSE && props.idExpense)
            return {type: "EXPENSE", id: props.idExpense};
        return null;
    };

    const handleUploadClick = async () => {
        if (!file) return;

        // Si la confirmation a déjà été donnée, on envoie directement.
        if (confirmOverwrite) {
            performUpload();
            return;
        }

        const ctx = getArchiveType();
        if (!ctx) {
            performUpload();
            return;
        }

        try {
            setChecking(true);
            const exists = await CheckArchiveExists(ctx.type, ctx.id);
            setChecking(false);
            if (exists) {
                setConfirmOverwrite(true);
            } else {
                performUpload();
            }
        } catch {
            // En cas d'erreur de vérification, on procède quand même au upload (comportement actuel).
            setChecking(false);
            performUpload();
        }
    };

    return (
        <form className="fcaForm" onSubmit={(e) => e.preventDefault()}>
            <div className="fcaField">
                <label>Veuillez sélectionner {targetLabel}</label>
                <div className="fcaDropZone" onClick={() => inputRef.current?.click()}>
                    <span className="material-symbols-outlined">upload_file</span>
                    <div className="fcaDropZoneTitle">
                        {file ? "Fichier sélectionné" : "Choisir un fichier"}
                    </div>
                    <div className="fcaDropZoneHint">{hint}</div>
                    {file && <div className="fcaDropZoneFileName">{file.name}</div>}
                    <input ref={inputRef} type="file" accept={accept}
                           style={{display: "none"}} onChange={handleFileChange}/>
                </div>
            </div>

            {confirmOverwrite && (
                <FCAlertMsg
                    activate={true}
                    type={TypeAlertMsg.WARNING}
                    title="Archive existante"
                    textMsg={`Un fichier est déjà archivé pour ${targetLabel}. Confirmez pour l'écraser.`}
                />
            )}

            <div className="fcaActions">
                <button type="button" className="fcaBtn fcaBtnSecondary" onClick={() => props.onFinished()}>
                    Annuler
                </button>
                <button type="button" className="fcaBtn fcaBtnPrimary"
                        onClick={handleUploadClick} disabled={!file || checking}>
                    <span className="material-symbols-outlined">check</span>
                    {confirmOverwrite ? "Confirmer l'écrasement" : (checking ? "Vérification…" : "Valider")}
                </button>
            </div>
        </form>
    );
};

export default FormUploadFile;
